import Input from "../inputs/Input";
import { AiOutlineLoading } from "react-icons/ai";
import { Alert, Button, Tooltip } from "flowbite-react";
import useCEP from "../../hooks/useCEP";
import { Controller, useForm } from "react-hook-form";
import { USERS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useState } from "react";
import CPFInput from "../inputs/CPFInput";
import PhoneInput from "../inputs/PhoneInput";
import { resetValuesForm } from "../../utils/utils";
import SelectInput from "../inputs/SelectInput";
import { HiInformationCircle } from "react-icons/hi";
import ImageInput from "../inputs/ImageInput";
import { useApplication } from "../../contexts/ApplicationContext";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiCalendar,
  FiShield,
} from "react-icons/fi";

/**
 * UserForm — versão moderna, clara e responsiva
 * - Layout em cartões/seções com títulos
 * - Grid responsivo 1/2 colunas
 * - Ações destacadas no rodapé
 * - Estados de erro elegantes
 */
const UserForm = ({
  user,
  fetchUsers,
  onClose,
  isOpen,
  type = "CREATE",
  companyId = null,
}) => {
  const { userPermission } = useApplication();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [avatarImg, setAvatarImg] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);

  const {
    register,
    getValues,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      cpf: "",
      phoneNumber: "",
      email: "",
      name: "",
      password: "",
    },
  });

  // CEP hook
  const { setCep, address, loading: loadingCEP } = useCEP();
  const onClickSearchCEP = () => setCep(getValues("addressPostalCode"));

  // Preenche endereço quando CEP resolve
  useEffect(() => {
    setValue("address", address?.logradouro);
    setValue("addressNeighborhood", address?.bairro);
    setValue("addressCity", address?.localidade);
  }, [address, setValue]);

  // Modo edição x criação
  useEffect(() => {
    if (type === "EDIT" && user) {
      Object.keys(user).forEach((k) => setValue(k, user[k]));
      setPreviewUrl(user?.imageUrl || "");
      setAvatarImg(user?.avatar || "");
    } else if (type === "CREATE") {
      resetValuesForm(["cpf", "phoneNumber"], setValue, reset);
      setPreviewUrl("");
      setAvatarImg("");
    }
    setValue("password", "");
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isOpen]);

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      if (type === "CREATE") {
        data.companyId = companyId;
        await axios.post(USERS.GET_USER, data);
      } else {
        if (data.password === "") delete data.password;
        await axios.put(`${USERS.GET_USER}/${user.id}`, data);
      }
      onClose?.(false);
      fetchUsers?.();
      if (type === "CREATE")
        resetValuesForm(["cpf", "phoneNumber"], setValue, reset);
    } catch (err) {
      setError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mx-auto max-w-4xl">
      {/* Header */}

      {/* Contato & Documento */}
      <div className="mb-6 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-base font-semibold text-slate-800">
          <FiPhone /> Contato & Documento
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Input
              type="text"
              label="Nome *"
              name="name"
              required={true}
              register={register}
              error={errors?.name && "Este campo é obrigatório"}
            />
          </div>
          <div>
            <Controller
              name="phoneNumber"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <PhoneInput
                  type="text"
                  label="Telefone *"
                  name={field.name}
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  required
                  error={fieldState.error && "Este campo é obrigatório"}
                />
              )}
            />
          </div>
          <div>
            <Controller
              name="cpf"
              control={control}
              render={({ field }) => (
                <CPFInput
                  type="text"
                  label="CPF"
                  name={field.name}
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </div>
          <div>
            <Input
              type="date"
              label="Data de nascimento"
              name="birthday"
              required={false}
              register={register}
            />
          </div>
        </div>
      </div>

      {/* Endereço */}
      <div className="mb-6 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-base font-semibold text-slate-800">
          <FiMapPin /> Endereço
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="md:col-span-2">
            <Input
              type="text"
              label="CEP"
              name="addressPostalCode"
              register={register}
            />
          </div>
          <div className="flex items-end">
            <Tooltip
              content="Busca automática de logradouro/bairro/cidade"
              placement="top"
            >
              <Button
                onClick={onClickSearchCEP}
                className="w-full md:w-auto"
                isProcessing={loadingCEP}
                processingSpinner={
                  <AiOutlineLoading className="h-5 w-5 animate-spin" />
                }
              >
                Buscar CEP
              </Button>
            </Tooltip>
          </div>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Input
            type="text"
            label="Endereço"
            name="address"
            register={register}
          />
          <Input
            type="text"
            label="Número"
            name="addressNumber"
            register={register}
          />
          <Input
            type="text"
            label="Bairro"
            name="addressNeighborhood"
            register={register}
          />
          <Input
            type="text"
            label="Cidade"
            name="addressCity"
            register={register}
          />
        </div>
      </div>

      {/* Acesso & Permissões */}
      {["ADMIN", "POWER_USER"].includes(userPermission) && (
        <div className="mb-6 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2 text-base font-semibold text-slate-800">
            <FiShield /> Acesso & Permissões
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              type="email"
              label={`Email *`}
              name="email"
              required
              register={register}
            />
            <Input
              type="password"
              label={`Senha ${type === "CREATE" ? "*" : ""}`}
              name="password"
              required={type === "CREATE"}
              register={register}
            />
            <div className="md:col-span-2">
              <SelectInput
                register={register}
                name="permissionId"
                label="Permissão de usuário"
              >
                {userPermission === "POWER_USER" && (
                  <option value="POWER_USER">Power User</option>
                )}
                <option value="ADMIN">Administrador</option>
                <option value="USER">Usuário</option>
                <option value="SELLER">Vendedor</option>
              </SelectInput>
            </div>
          </div>
        </div>
      )}

      {/* Rodapé de ações */}
      <div className="sticky bottom-2 z-10 mt-8 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white/80 p-3 backdrop-blur supports-[backdrop-filter]:bg-white/60 md:static md:border-0 md:bg-transparent md:p-0">
        {error && (
          <Alert
            color="failure"
            icon={HiInformationCircle}
            className="order-2 md:order-1"
          >
            {error?.config?.method !== "get"
              ? `Erro ao ${
                  error?.config?.method === "put" ? "atualizar" : "criar"
                } usuário`
              : "E-mail já cadastrado"}
          </Alert>
        )}
        <div className="order-1 flex flex-col-reverse gap-3 md:order-2 md:flex-row md:justify-end">
          <Button
            color="light"
            onClick={() => onClose?.(false)}
            type="button"
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="primary"
            isProcessing={isSubmitting}
            processingSpinner={
              <AiOutlineLoading className="h-6 w-6 animate-spin" />
            }
            disabled={isSubmitting}
          >
            {type === "CREATE" ? "Criar" : "Atualizar"} usuário
          </Button>
        </div>
      </div>
    </form>
  );
};

export default UserForm;
